import {
  LdtOrderDetailView,
  LdtOrderRepository,
} from '@ldt/api/order_repository';
import type { LdtOrderUpdateOptions } from '@ldt/types/order';
import { GraphqlClient } from 'src/api/graphql_client';
import { GraphqlOrderRepository } from 'src/api/graphql_order_repository';
import type { DownloadReportResult } from 'src/api/order_repository';
import {
  SpecimenType,
  SubmitOrderInput,
  LdtOrderDetailDirectUploadLabTechFieldsFragment,
  LdtOrderDetailFieldsFragment,
  LdtOrderForReviseReportFieldsFragment,
  OrderStatus,
  OrderingProviderFieldsFragment,
  UpdateOrderInput,
  CreateOrderInput,
  LdtOrdersQuery,
  LdtOrdersQueryVariables,
  LdtOrdersDocument,
  CreateLdtOrderMutation,
  CreateLdtOrderDocument,
  UpdateLdtOrderMutation,
  UpdateLdtOrderDocument,
  SubmitLdtOrderMutationVariables,
  SubmitLdtOrderMutation,
  SubmitLdtOrderDocument,
  LdtOrderDocument,
  LdtOrderQuery,
  LdtOrderDirectUploadLabTechDocument,
  LdtOrderDirectUploadLabTechQuery,
  LdtOrderingProvidersQuery,
  LdtOrderingProvidersQueryVariables,
  LdtOrderingProvidersDocument,
  CreatePatientInput,
  CreateOrderingProviderInput,
  CreateOrderingProviderMutation,
  CreateOrderingProviderDocument,
  UpdateOrderingProviderInput,
  UpdateOrderingProviderMutation,
  UpdateOrderingProviderDocument,
  DeleteOrderingProviderMutation,
  DeleteOrderingProviderDocument,
  IsValidOrderingPhysicianEmailQuery,
  IsValidOrderingPhysicianEmailDocument,
  UpdateOrderBillingHoldMutation,
  FaxFieldsFragment,
  PathologyLabFieldsFragment,
  PathologyLabsQuery,
  PathologyLabsQueryVariables,
  PathologyLabsDocument,
  SetOrderPathologyLabMutation,
  SetOrderPathologyLabDocument,
  SetOrderPathologyLabInput,
  DeleteLdtFileMutation,
  DeleteLdtFileDocument,
  FileDownloadUrlQuery,
  FileDownloadUrlDocument,
  UpdateOrderStatusQcApprovedMutation,
  UpdateOrderStatusQcApprovedDocument,
  UpdateOrderStatusMutation,
  UpdateOrderStatusDocument,
  QcApproveSlideImageMutation,
  QcApproveSlideImageDocument,
  QcRejectSlideImageMutation,
  QcRejectSlideImageDocument,
  FaxTestReportToPathologyLabMutation,
  FaxTestReportToPathologyLabDocument,
  FaxTestReportToOrderingProviderMutation,
  FaxTestReportToOrderingProviderDocument,
  ShareTrrMutation,
  ShareTrrDocument,
  LdtOrderForReviseReportQuery,
  LdtOrderForReviseReportDocument,
  ReviseReportMutation,
  ReviseReportDocument,
  ReviseReportInput,
  RefaxPathologyFilesDocument,
  RefaxPathologyFilesMutationVariables,
  RefaxPathologyFilesMutation,
  MostRecentFaxDocument,
  MostRecentFaxQueryVariables,
  MostRecentFaxQuery,
  FaxType,
  LdtRejectReportMutation,
  LdtRejectReportDocument,
  UpdateOrderBillingHoldInput,
  UpdateRejectedOrderDocument,
  UpdateOrderBillingHoldDocument,
  CreateLdtMultipartUploadDocument,
  CreateLdtMultipartUploadMutation,
  SlideImageScannerType,
  SlideMetadataInputType,
  UploadedFileCategory,
  PathologyFaxAction,
} from 'src/types';
import type {
  DownloadTestReportQuery,
  UpdateRejectedOrderMutation,
} from 'src/types';
import { DownloadReportErrorCode, DownloadTestReportDocument } from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { CursorPaginatedResponse } from 'src/types/pagination';

/**
 * Implementation of the LDT order repository for a GraphQL backend.
 */
export class LdtGraphqlOrderRepository
  extends GraphqlOrderRepository<LdtOrderDetailFieldsFragment>
  implements LdtOrderRepository
{
  readonly _platform = DistributionPlatform.Ldt;

  constructor(graphqlClient: GraphqlClient) {
    super(graphqlClient);
  }

  async getOrders(
    ordersQueryVariables: LdtOrdersQueryVariables,
  ): Promise<LdtOrdersQuery> {
    const pageIndex = Math.max(0, ordersQueryVariables.page - 1);
    const response = await this._graphqlClient.request<LdtOrdersQuery>(
      LdtOrdersDocument,
      {
        ...ordersQueryVariables,
        page: pageIndex,
      },
    );

    return response;
  }

  async getOrderById(
    id: string,
    options: { view: LdtOrderDetailView },
  ): Promise<
    | LdtOrderDetailFieldsFragment
    | LdtOrderDetailDirectUploadLabTechFieldsFragment
    | null
  > {
    if (options.view === LdtOrderDetailView.DirectUploadLabTech) {
      const response =
        await this._graphqlClient.request<LdtOrderDirectUploadLabTechQuery>(
          LdtOrderDirectUploadLabTechDocument,
          { orderId: id },
        );
      return response.order ?? null;
    }

    const response = await this._graphqlClient.request<LdtOrderQuery>(
      LdtOrderDocument,
      { orderId: id },
    );

    return response.order ?? null;
  }

  async create(input: CreateOrderInput): Promise<LdtOrderDetailFieldsFragment> {
    const response = await this._graphqlClient.request<CreateLdtOrderMutation>(
      CreateLdtOrderDocument,
      {
        order: {
          ...input,
          startedAt: input.startedAt || new Date().toISOString(),
        },
      },
    );

    return response.createOrder!.order!;
  }

  async update(
    order: LdtOrderDetailFieldsFragment,
    options?: LdtOrderUpdateOptions,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const input = this.buildUpdateOrderInput(order, options);

    const response = await this._graphqlClient.request<UpdateLdtOrderMutation>(
      UpdateLdtOrderDocument,
      {
        order: input as UpdateOrderInput,
      },
    );

    return response.updateOrder!.order!;
  }

  async updateRejectedOrder(
    order: LdtOrderDetailFieldsFragment,
    revertStatus: OrderStatus,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const input = this.buildUpdateOrderInput(order);
    const response =
      await this._graphqlClient.request<UpdateRejectedOrderMutation>(
        UpdateRejectedOrderDocument,
        { input, newStatus: revertStatus },
      );

    return response.updateRejectedOrder!.order!;
  }

  private buildUpdateOrderInput(
    order: LdtOrderDetailFieldsFragment,
    options?: LdtOrderUpdateOptions,
  ): Partial<UpdateOrderInput> {
    const patientInput = {
      firstNamePhi: order.patient?.firstNamePhi,
      lastNamePhi: order.patient?.lastNamePhi,
      dateOfBirthPhi: order.patient?.dateOfBirthPhi,
      ethnicity: order.patient?.ethnicity,
    } as CreatePatientInput;
    const input: Partial<UpdateOrderInput> = {
      id: order.id,
      specimenType: order.specimenType,
      orderingProviderId: order.orderingProvider?.id,
      patient: patientInput,
      primaryDiagnosisCodes: order.primaryDiagnosisCodes,
      customPrimaryDiagnosisCode: order.customPrimaryDiagnosisCode,
      qcRevisionReason: order.qcRevisionReason,
      labProcedureDate: order.labProcedureDatePhi,
      patientLabAccession: order.patientLabAccession,
      slideSamplePart: order.slideSamplePart,
    };

    if (order.specimenType === SpecimenType.ProstateBiopsy) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.nccnRiskCategory = order.nccnRiskCategory;
      input.totalCores = order.totalCores;
      input.positiveCores = order.positiveCores;
      input.patientStatusAtBiopsy = order.patientStatusAtBiopsy;
      input.patientDischargeDate = order.patientDischargeDate;
      input.consentToBillPatient = order.consentToBillPatient;
    } else if (order.specimenType === SpecimenType.ProstatePostRp) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.surgicalMargin = order.surgicalMargin;
      input.patientStatusAtBiopsy = order.patientStatusAtBiopsy;
      input.patientDischargeDate = order.patientDischargeDate;
    } else if (order.specimenType === SpecimenType.MetastaticProstateBiopsy) {
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.patientStatusAtBiopsy = order.patientStatusAtBiopsy;
      input.patientDischargeDate = order.patientDischargeDate;
      input.metastaticDiagnosisCodes = order.metastaticDiagnosisCodes;
      input.customMetastaticDiagnosisCode = order.customMetastaticDiagnosisCode;
    } else {
      throw new Error('Unsupported order type');
    }

    const sendTempusEmail = options?.sendTempusMissingClinicalVariablesEmail;
    // Only sent when CS saves with incomplete clinical vars; backend ignores
    // this flag when clinical data is complete.
    if (sendTempusEmail !== undefined) {
      input.sendTempusMissingClinicalVariablesEmail = sendTempusEmail;
    }

    return input;
  }

  async submit(
    order: LdtOrderDetailFieldsFragment,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const patientInput = {
      firstNamePhi: order.patient?.firstNamePhi || undefined,
      lastNamePhi: order.patient?.lastNamePhi || undefined,
      dateOfBirthPhi: order.patient?.dateOfBirthPhi || undefined,
      ethnicity: order.patient?.ethnicity || undefined,
    } as CreatePatientInput;
    const input: Partial<SubmitOrderInput> = {
      id: order.id,
      specimenType: order.specimenType,
      orderingProviderId: order.orderingProvider?.id,
      patient: patientInput,
      primaryDiagnosisCodes: order.primaryDiagnosisCodes,
      customPrimaryDiagnosisCode: order.customPrimaryDiagnosisCode,
      labProcedureDate: order.labProcedureDatePhi,
      patientLabAccession: order.patientLabAccession,
      slideSamplePart: order.slideSamplePart,
    };

    // Conditionally include fields based on order type and region
    if (order.specimenType === SpecimenType.ProstateBiopsy) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.nccnRiskCategory = order.nccnRiskCategory;
      input.totalCores = order.totalCores;
      input.positiveCores = order.positiveCores;
      input.patientStatusAtBiopsy = order.patientStatusAtBiopsy;
      input.patientDischargeDate = order.patientDischargeDate;
    } else if (order.specimenType === SpecimenType.ProstatePostRp) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.surgicalMargin = order.surgicalMargin;
      input.patientStatusAtBiopsy = order.patientStatusAtBiopsy;
      input.patientDischargeDate = order.patientDischargeDate;
    } else if (order.specimenType === SpecimenType.MetastaticProstateBiopsy) {
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.patientStatusAtBiopsy = order.patientStatusAtBiopsy;
      input.patientDischargeDate = order.patientDischargeDate;
      input.metastaticDiagnosisCodes = order.metastaticDiagnosisCodes;
      input.customMetastaticDiagnosisCode = order.customMetastaticDiagnosisCode;
    } else {
      throw new Error('Unsupported order type');
    }

    const variables: SubmitLdtOrderMutationVariables = {
      input: input as SubmitOrderInput,
    };

    const response = await this._graphqlClient.request<SubmitLdtOrderMutation>(
      SubmitLdtOrderDocument,
      variables,
    );

    return response.submitOrder!.order!;
  }

  async getOrderingProviders(params: {
    searchText?: string;
    cursor?: string | null;
    first?: number;
  }): Promise<CursorPaginatedResponse<OrderingProviderFieldsFragment>> {
    const { searchText, cursor, first = 15 } = params;

    const variables: LdtOrderingProvidersQueryVariables = {
      searchText: searchText ?? null,
      after: cursor || null,
      first,
    };

    const response =
      await this._graphqlClient.request<LdtOrderingProvidersQuery>(
        LdtOrderingProvidersDocument,
        variables,
      );

    const connection = response.orderingProviders;
    if (!connection) {
      return {
        items: [],
        nextCursor: null,
        hasNextPage: false,
      };
    }

    const items =
      connection.edges
        ?.map((edge) => edge?.node)
        .filter(
          (node): node is OrderingProviderFieldsFragment => node !== null,
        ) ?? [];

    return {
      items,
      nextCursor: connection.pageInfo?.endCursor ?? null,
      hasNextPage: connection.pageInfo?.hasNextPage ?? false,
    };
  }

  async createOrderingProvider(
    input: CreateOrderingProviderInput,
  ): Promise<OrderingProviderFieldsFragment> {
    const response =
      await this._graphqlClient.request<CreateOrderingProviderMutation>(
        CreateOrderingProviderDocument,
        {
          input,
        },
      );

    return response.createOrderingProvider!.orderingProvider!;
  }

  async updateOrderingProvider(
    input: UpdateOrderingProviderInput,
  ): Promise<OrderingProviderFieldsFragment> {
    const response =
      await this._graphqlClient.request<UpdateOrderingProviderMutation>(
        UpdateOrderingProviderDocument,
        {
          input,
        },
      );

    return response.updateOrderingProvider!.orderingProvider!;
  }

  async deleteOrderingProvider(id: string): Promise<void> {
    await this._graphqlClient.request<DeleteOrderingProviderMutation>(
      DeleteOrderingProviderDocument,
      {
        id,
      },
    );
  }

  async validateOrderingPhysicianEmail(
    email: string,
  ): Promise<{ isValid: boolean; isAlreadyUsed: boolean }> {
    const response =
      await this._graphqlClient.request<IsValidOrderingPhysicianEmailQuery>(
        IsValidOrderingPhysicianEmailDocument,
        {
          email,
        },
      );

    const validation = response.isValidOrderingPhysicianEmail;
    return {
      isValid: validation?.isValid ?? false,
      isAlreadyUsed: validation?.isAlreadyUsed ?? false,
    };
  }

  async getPathologyLabs(params: {
    searchText?: string;
    cursor?: string | null;
    first?: number;
  }): Promise<CursorPaginatedResponse<PathologyLabFieldsFragment>> {
    const { searchText, cursor, first = 15 } = params;

    const variables: PathologyLabsQueryVariables = {
      siteName: searchText ?? null,
      after: cursor || null,
      first,
    };

    const response = await this._graphqlClient.request<PathologyLabsQuery>(
      PathologyLabsDocument,
      variables,
    );

    const connection = response.salesforce?.pathologyLabs;
    if (!connection) {
      return {
        items: [],
        nextCursor: null,
        hasNextPage: false,
      };
    }

    const items =
      connection.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is PathologyLabFieldsFragment => node !== null) ??
      [];

    return {
      items,
      nextCursor: connection.pageInfo?.endCursor ?? null,
      hasNextPage: connection.pageInfo?.hasNextPage ?? false,
    };
  }

  async updateOrderPathologyLab(
    orderId: string,
    pathologyLabSalesforceId: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const input: SetOrderPathologyLabInput = {
      id: orderId,
      pathologyLabSalesforceId,
    };

    const response =
      await this._graphqlClient.request<SetOrderPathologyLabMutation>(
        SetOrderPathologyLabDocument,
        {
          input,
        },
      );

    return response.setOrderPathologyLab!.order!;
  }

  async deleteFile(fileId: string): Promise<void> {
    await this._graphqlClient.request<DeleteLdtFileMutation>(
      DeleteLdtFileDocument,
      {
        id: fileId,
      },
    );
  }

  async getFileDownloadUrl(fileId: string): Promise<string> {
    const response = await this._graphqlClient.request<FileDownloadUrlQuery>(
      FileDownloadUrlDocument,
      {
        id: fileId,
      },
    );

    return response.fileDownloadUrl!;
  }

  async updateOrderStatusQCApproved(params: {
    orderId: string;
    note?: string | null;
    pathologyFaxAction?: PathologyFaxAction | null;
    uploadedFileIds?: string[] | null;
  }): Promise<LdtOrderDetailFieldsFragment> {
    const response =
      await this._graphqlClient.request<UpdateOrderStatusQcApprovedMutation>(
        UpdateOrderStatusQcApprovedDocument,
        {
          id: params.orderId,
          note: params.note ?? null,
          pathologyFaxAction: params.pathologyFaxAction ?? null,
          uploadedFileIds: params.uploadedFileIds ?? null,
        },
      );

    return response.updateOrderStatusQCApproved!.order!;
  }

  async updateOrderStatus(
    orderId: string,
    status: OrderStatus,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const response =
      await this._graphqlClient.request<UpdateOrderStatusMutation>(
        UpdateOrderStatusDocument,
        {
          id: orderId,
          status,
        },
      );

    return response.updateOrderStatus!.order!;
  }

  async qcApproveSlideImage(
    orderId: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const response =
      await this._graphqlClient.request<QcApproveSlideImageMutation>(
        QcApproveSlideImageDocument,
        { orderId },
      );

    return response.qcApproveSlideImage!.order!;
  }

  async qcRejectSlideImage(
    orderId: string,
    reason: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const response =
      await this._graphqlClient.request<QcRejectSlideImageMutation>(
        QcRejectSlideImageDocument,
        { orderId, reason },
      );

    return response.qcRejectSlideImage!.order!;
  }

  async faxTestReportToPathologyLab(
    orderId: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const response =
      await this._graphqlClient.request<FaxTestReportToPathologyLabMutation>(
        FaxTestReportToPathologyLabDocument,
        {
          orderId,
        },
      );

    return response.faxTestReportToPathologyLab!.order!;
  }

  async faxTestReportToOrderingProvider(
    orderId: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const response =
      await this._graphqlClient.request<FaxTestReportToOrderingProviderMutation>(
        FaxTestReportToOrderingProviderDocument,
        {
          orderId,
        },
      );

    return response.faxTestReportToOrderingProvider!.order!;
  }

  async shareTrr(
    orderId: string,
    emails: string[],
  ): Promise<LdtOrderDetailFieldsFragment> {
    const response = await this._graphqlClient.request<ShareTrrMutation>(
      ShareTrrDocument,
      {
        id: orderId,
        emails,
      },
    );

    return response.shareTrr!.order!;
  }

  async downloadTRR(
    orderId: string,
    _signed: boolean,
    isDeidentified: boolean,
  ): Promise<DownloadReportResult> {
    const response = await this._graphqlClient.request<DownloadTestReportQuery>(
      DownloadTestReportDocument,
      { id: orderId, deidentified: isDeidentified },
    );

    const finalReportUrl = response.downloadTestReport?.finalReportUrl;
    if (!finalReportUrl) {
      return {
        success: false,
        code: DownloadReportErrorCode.ReportNotAvailable,
      };
    }

    return { success: true, url: finalReportUrl };
  }

  async getOrderForReviseReport(
    orderId: string,
  ): Promise<LdtOrderForReviseReportFieldsFragment | null> {
    const response =
      await this._graphqlClient.request<LdtOrderForReviseReportQuery>(
        LdtOrderForReviseReportDocument,
        { orderId },
      );

    return response.order ?? null;
  }

  async reviseReport(
    input: ReviseReportInput,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const response = await this._graphqlClient.request<ReviseReportMutation>(
      ReviseReportDocument,
      { input },
    );

    return response.reviseReport!.order!;
  }

  async refaxPathologyFiles(params: {
    orderId: string;
    pathologyFaxAction: PathologyFaxAction;
    note?: string | null;
    uploadedFileIds?: string[] | null;
  }): Promise<LdtOrderDetailFieldsFragment> {
    const variables: RefaxPathologyFilesMutationVariables = {
      orderId: params.orderId,
      pathologyFaxAction: params.pathologyFaxAction,
      note: params.note ?? null,
      uploadedFileIds: params.uploadedFileIds ?? null,
    };

    const response =
      await this._graphqlClient.request<RefaxPathologyFilesMutation>(
        RefaxPathologyFilesDocument,
        variables,
      );

    return response.refaxPathologyFiles!.order!;
  }

  async getMostRecentFax(
    orderId: string,
    faxType: FaxType,
  ): Promise<FaxFieldsFragment | null> {
    const variables: MostRecentFaxQueryVariables = {
      orderId: orderId,
      faxType: faxType,
    };

    const response = await this._graphqlClient.request<MostRecentFaxQuery>(
      MostRecentFaxDocument,
      variables,
    );

    return response.mostRecentFax;
  }

  async rejectReport(
    orderId: string,
    reason: string,
    newStatus: OrderStatus | null,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const response = await this._graphqlClient.request<LdtRejectReportMutation>(
      LdtRejectReportDocument,
      {
        orderId,
        newStatus: newStatus,
        reason,
      },
    );

    return response.ldtRejectReport!.order!;
  }

  async updateBillingHold(
    orderId: string,
    hasBillingHold: boolean,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const input: UpdateOrderBillingHoldInput = {
      id: orderId,
      hasBillingHold,
    };
    const response =
      await this._graphqlClient.request<UpdateOrderBillingHoldMutation>(
        UpdateOrderBillingHoldDocument,
        { input },
      );

    return response.updateOrderBillingHold!.order!;
  }

  async createMultipartUpload(
    filename: string,
    category: UploadedFileCategory,
    orderId: string,
    countParts: number,
    signal?: AbortSignal,
    slideMetadata?: SlideMetadataInputType,
    scannerType?: SlideImageScannerType,
  ): Promise<string> {
    const response =
      await this._graphqlClient.request<CreateLdtMultipartUploadMutation>(
        CreateLdtMultipartUploadDocument,
        {
          filename,
          category,
          orderId,
          countParts,
          slideMetadata: slideMetadata ?? undefined,
          scannerType: scannerType ?? undefined,
        },
        undefined,
        signal,
      );

    return response.createMultipartUpload!.uploadedFile.id;
  }
}
